import { ComponentType } from '@angular/cdk/overlay';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/**
 * Opens a dialog and wires the shared app-level behavior: Escape does not propagate
 * to underlying handlers and the browser back button (popstate) closes the dialog.
 *
 * Kept free of feature-dialog imports so any service can use it without creating import cycles.
 */
export function openTrackedDialog<T, R>(
  matDialog: MatDialog,
  destroyRef: DestroyRef,
  component: ComponentType<T>,
  config?: MatDialogConfig
): MatDialogRef<T, R> {
  const dialogRef = matDialog.open<T, unknown, R>(component, config);

  dialogRef.keydownEvents().subscribe(event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
    }
  });

  if (!history.state?.dialogIds) {
    history.replaceState({ dialogIds: [dialogRef.id] }, '');
  } else {
    history.state.dialogIds.push(dialogRef.id);
  }

  const onPopState = (): void => dialogRef.close();
  window.addEventListener('popstate', onPopState);
  dialogRef
    .afterClosed()
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() => {
      window.removeEventListener('popstate', onPopState);

      const index = history.state.dialogIds?.indexOf(dialogRef.id);

      if (index > 0) {
        history.state.dialogIds.splice(index, 1);
      }
    });
  return dialogRef;
}
